import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../api/client'

/**
 * The per-user e-mail signature (spec 2026-09-15-02 §3).
 *
 * PLAIN TEXT, never HTML: the mail templates escape it and render it in a
 * white-space:pre-wrap block, like a message body, so nothing typed here can
 * reach the markup.
 *
 * Own profile goes through `/users/me/settings/...`, which is gated on
 * `organization.read` rather than `contacts.read` — the same reason
 * useFavoriteAssets moved: a member must be able to write their own sign-off
 * without permission to read the contact book. A manager editing a colleague's
 * uses the `/users/{uid}` twin, which does need `contacts`.
 */
export const EMAIL_SIGNATURE_SETTING = 'email_signature'

export const EMAIL_SIGNATURE_MAX_LENGTH = 600

export function useEmailSignature(org: string, userUid: string, isOwnProfile: boolean) {
    const queryClient = useQueryClient()

    // The uid stays in the key whichever path is used, so a manager's edit and
    // the owner's own view invalidate the same entry.
    const queryKey = ['userSetting', org, userUid, EMAIL_SIGNATURE_SETTING]

    const { data: signature = '', isLoading } = useQuery({
        queryKey,
        queryFn: async () => {
            const response = isOwnProfile
                ? await apiClient.GET(
                      '/api/v1/organizations/{orgCode}/users/me/settings/{settingCode}',
                      { params: { path: { orgCode: org, settingCode: EMAIL_SIGNATURE_SETTING } } }
                  )
                : await apiClient.GET(
                      '/api/v1/organizations/{orgCode}/users/{userUid}/settings/{settingCode}',
                      { params: { path: { orgCode: org, userUid, settingCode: EMAIL_SIGNATURE_SETTING } } }
                  )
            if (response.error) {
                // 404 is "never set one", which is an empty signature and not
                // an error: the mail falls back to the name + organization.
                return ''
            }
            const data = response.data as any
            const value = data?.value ?? data
            return typeof value === 'string' ? value : ''
        },
        enabled: !!org && (isOwnProfile || !!userUid),
        staleTime: 5 * 60 * 1000,
        retry: 1,
    })

    const mutation = useMutation({
        mutationFn: async (next: string) => {
            const response = isOwnProfile
                ? await apiClient.PUT(
                      '/api/v1/organizations/{orgCode}/users/me/settings/{settingCode}',
                      {
                          params: { path: { orgCode: org, settingCode: EMAIL_SIGNATURE_SETTING } },
                          body: next as any,
                      }
                  )
                : await apiClient.PUT(
                      '/api/v1/organizations/{orgCode}/users/{userUid}/settings/{settingCode}',
                      {
                          params: { path: { orgCode: org, userUid, settingCode: EMAIL_SIGNATURE_SETTING } },
                          body: next as any,
                      }
                  )
            if (response.error) {
                throw new Error('Failed to update the e-mail signature')
            }
            return next
        },
        onSuccess: (next: string) => {
            queryClient.setQueryData(queryKey, next)
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey })
        },
    })

    return { signature, isLoading, saveSignature: mutation.mutateAsync, isSaving: mutation.isPending }
}
